import { faQuestionCircle } from '@fortawesome/pro-solid-svg-icons'

import { Accordion } from '@b-stock/fe-core/bstock-react'

import { myAccountSettingsBase } from '@/helpers/myAccountNav'
import { trackSignUpStart } from '@/helpers/telemetry/trackSignUpStart'

import {
  Container,
  SubHeader,
  TextBold,
  StyledParagraph,
  StyledAccordionWrapper,
  AnswerContainer,
  StyledIcon,
  QuestionLabel,
  Section,
  StyledLink,
  List,
  OrderedList,
  ListItem,
  SubList,
} from './ui'

type Faq = {
  question: string
  answer: string | React.ReactNode
}

export type FaqSection = {
  title: string
  questions: Faq[]
}

// This will need to be revisited if we have additional sellers with different faqs. For now, this is sufficient for only Costco.
export const getFaqsForCostco = (
  _sellerName: string,
  myAccountHpNav: boolean
): FaqSection[] =>
  [
    {
      title: 'Applying & Logging In',
      questions: [
        {
          question:
            'How do I apply for the Costco Liquidations marketplace? Is there any cost to me?',
          answer: (
            <>
              <StyledLink
                href="/acct/signup?siteAbb=cst"
                onClick={() => trackSignUpStart('costco_faq')}
              >
                Click here to create an account
              </StyledLink>
              . Fill in the required fields, attach a valid reseller&apos;s
              certificate and click the submit button. We will notify you within
              one business day if your account is approved. There is no fee for
              applying or for being an approved buyer on{' '}
              <StyledLink href="https://bstock.com/buy/seller/costco">
                Costco Liquidations
              </StyledLink>{' '}
              .
            </>
          ),
        },
        {
          question: 'Can I apply if I am located outside of the United States?',
          answer:
            'Yes. International buyers will need to provide a Proof of Business, when applying for marketplace approval. Please ensure that you provide a valid US address, as shipment must be accepted in the US.',
        },
        {
          question:
            'I am an exporter located in the United States. Can I apply?',
          answer:
            "A reseller's certificate is required for approval of any buyer located in the United States, not including states that do not charge sales tax.",
        },
        {
          question:
            "What is a reseller's certificate and why do I need it? Do all states require a reseller's certificate?",
          answer: (
            <>
              A reseller&apos;s certificate exempts buyers from paying sales tax
              on their purchases. We never collect sales tax on behalf of our
              sellers, and only sell to buyers who have state issued
              reseller&apos;s certificates. Users in any state that has a sales
              tax must submit a reseller&apos;s certificate to be approved on{' '}
              <StyledLink href="https://bstock.com/buy/seller/costco">
                Costco Liquidations
              </StyledLink>{' '}
              . This may also be called a Seller&apos;s Permit, Sales Tax
              License, Sales and Use Tax Permit, Certificate of Authority or a
              similar name for your state. For instructions on how to obtain a
              reseller&apos;s certificate, example of images or a blank form for
              your state, please visit our{' '}
              <StyledLink href="/faq/buyer-faq/applying-logging-in/how-do-i-get-a-resale-certificate/">
                reseller certificate instructions page
              </StyledLink>
              . If you live in Alaska, Delaware, Montana, New Hampshire or
              Oregon, this is not required as part of your application.
            </>
          ),
        },
        {
          question: 'Can I submit my EIN (Employer Identification Number)?',
          answer: (
            <>
              No. An EIN is a federal identification number. A state issued
              certificate is required for approval on{' '}
              <StyledLink href="https://bstock.com/buy/seller/costco">
                Costco Liquidations
              </StyledLink>
              .
            </>
          ),
        },
        {
          question: "How do I get a reseller's certificate?",
          answer: (
            <>
              For instructions on how to obtain a reseller&apos;s certificate
              for your state, please visit our{' '}
              <StyledLink href="/faq/buyer-faq/applying-logging-in/how-do-i-get-a-resale-certificate/">
                reseller certificate instructions page
              </StyledLink>
              .
            </>
          ),
        },
        {
          question: "How do I submit my reseller's certificate?",
          answer: (
            <>
              Your resale certificate can be uploaded during the registration
              process for{' '}
              <StyledLink href="https://bstock.com/buy/seller/costco">
                Costco Liquidations
              </StyledLink>
              .
            </>
          ),
        },
        {
          question: "Why hasn't my account been approved yet?",
          answer: (
            <>
              Please allow one full business day for your application to be
              approved. If you have not been approved, it is likely because we
              have not received the proper reseller form from you. For
              instructions on how to obtain a reseller&apos;s certificate,
              example of images or a blank form for your state, please visit our{' '}
              <StyledLink href="/faq/buyer-faq/applying-logging-in/how-do-i-get-a-resale-certificate/">
                reseller certificate instructions page
              </StyledLink>
              .
            </>
          ),
        },
      ],
    },
    {
      title: 'Bidding',
      questions: [
        {
          question: 'How do I bid?',
          answer: (
            <>
              Once you are an approved buyer on{' '}
              <StyledLink href="https://bstock.com/buy/seller/costco">
                Costco Liquidations
              </StyledLink>{' '}
              , you can go to any auction page and enter the highest amount that
              you are willing to pay. Enter the amount in the white box to the
              right of &quot;My Maximum Bid&quot;. Below, you will see the
              minimum allowable bid amount. Once the amount has been entered,
              click the &quot;Bid Now&quot; button. Please note the message near
              the top of the page, which indicates if your bid is high enough to
              become the current high bid. If not, you may wish to increase your
              bid. For each bid you enter, you will receive an email
              confirmation that your bid was successfully placed.
            </>
          ),
        },
        {
          question: 'What condition is the merchandise in? Is it all new?',
          answer: (
            <>
              The condition of merchandise can vary. Please view the listing
              page for any information provided, or visit our About page for
              details on each condition type.
            </>
          ),
        },
        {
          question: 'Why do auctions extend at the end?',
          answer: (
            <>
              If a bid is placed in the last 5 minutes of an auction, the
              auction end time will extend for an additional 3 minutes. This is
              called &quot;Popcorn Bidding&quot; and gives all bidders an equal
              chance of winning an auction by extending the end time of the
              auction if a last minute bid is placed. Popcorn Bidding is used to
              simulate a live auction and prevents other bidders from
              &quot;sniping&quot; an auction at the last second.
            </>
          ),
        },
        {
          question:
            'How can I enter my maximum bid without it appearing to other bidders?',
          answer: (
            <>
              <StyledParagraph>
                All bids placed on{' '}
                <StyledLink href="https://bstock.com/buy/seller/costco">
                  Costco Liquidations
                </StyledLink>{' '}
                act as &quot;proxy&quot; bids. You can bid the highest amount
                you are willing to bid and allow the system to manage the
                bidding for you. The Current Bid displayed on each auction is
                equivalent to the second highest bidder&apos;s bid, plus one bid
                increment ($25 on{' '}
                <StyledLink href="https://bstock.com/buy/seller/costco">
                  Costco Liquidations
                </StyledLink>
                ). If the high bidder has placed a higher bid, it will not
                display. This allows bidders to know that if they place a proxy
                bid, they could still win the auction for any amount below or at
                their maximum bid.
              </StyledParagraph>
              <StyledParagraph>
                <TextBold>Example:</TextBold> If you find an item that you are
                interested in that is currently at $1,000 without any bids and
                choose to bid $5,000, you will be winning the auction at $1,000.
                If another bidder places a bid for $1,100, you will be winning
                the auction at $1,125, one increment higher. The system will
                continue to automatically outbid other bidders on your half, but
                will never exceed your proxy bid of $5,000.
              </StyledParagraph>
            </>
          ),
        },
        {
          question: 'Can I buy merchandise without bidding on it?',
          answer: (
            <>
              Currently, all merchandise sold on{' '}
              <StyledLink href="https://bstock.com/buy/seller/costco">
                Costco Liquidations
              </StyledLink>{' '}
              is in auction format, only. If you only want to place one bid, you
              can always place a proxy bid.
            </>
          ),
        },
        {
          question: 'Can I cancel my bid?',
          answer: (
            <>
              <StyledParagraph>
                Please remember that every bid on B-Stock operated marketplaces
                is a binding contract. You are agreeing to buy the lot at the
                price you indicate should you ultimately be the high bidder.
                Bids may not be cancelled once submitted. If you accidentally
                enter a wrong bid amount (for example, you entered $1000 instead
                of $100) you can click on the Cancel link within the bid
                confirmation pop-up window to change your bid amount prior to
                submission. Changing your mind does not qualify as accidentally
                entering a wrong bid amount.
              </StyledParagraph>
              <StyledParagraph>
                Your bids affect how other users bid within the auction and the
                auction&apos;s final selling price. We provide ample opportunity
                for you to check and double check your bid before submitting, so
                requests to cancel bids will most likely result in the
                suspension of your account.
              </StyledParagraph>
              <StyledParagraph>
                A record of all bids is kept on file. If a current winning bid
                is disqualified in an auction for any reason, B-Stock will
                restore the next highest non-winning bid to winning status. This
                decision is made at the sole discretion of B-Stock Solutions.
              </StyledParagraph>
            </>
          ),
        },
        {
          question:
            'Are the images on the site actual images of the merchandise?',
          answer: (
            <>
              Merchandise may be displayed in its current condition, or with a
              stock image. If it is unclear in any way,{' '}
              <StyledLink href="/contact-us/">contact us</StyledLink> prior to
              bidding.
            </>
          ),
        },
        {
          question: 'Can I inspect the merchandise before bidding?',
          answer:
            'Merchandise cannot be inspected prior to bidding. All available information is detailed on the auction page.',
        },
        {
          question:
            'Why did I lose the auction if I bid the same amount as another bidder?',
          answer:
            'In order to win an auction, you need to bid a higher amount than the current high bidder. If both you and another buyer placed maximum bids of $1,000, but the other bidder placed their bid before yours, they will win the auction.',
        },
        {
          question: 'How do I know if I win?',
          answer: (
            <>
              At the close of the auction, the winning bidder is notified by
              email. This will also show up as an order in{' '}
              <StyledLink href={myAccountSettingsBase(myAccountHpNav)}>
                your account
              </StyledLink>{' '}
              under the &quot;Orders&quot; tab in the top right of the page.
            </>
          ),
        },
      ],
    },
    {
      title: 'Payment & Shipping',
      questions: [
        {
          question: 'How do I pay? How long do I have to pay?',
          answer: (
            <>
              <StyledParagraph>
                All Auctions must be paid either by wire transfer or ACH, within
                2 business days. Wire and ACH instructions will be included on
                the order details page.
              </StyledParagraph>
              <StyledParagraph>
                If you are unfamiliar with sending a wire, you can easily do it
                from any bank (for a cost of about $30), and most banks allow
                you to send wires online.{' '}
                <TextBold>
                  FULL WIRE INSTRUCTIONS ARE AVAILABLE ON EACH AUCTION
                  DESCRIPTION PAGE AND WILL ALSO BE PROVIDED IF YOU WIN AN
                  AUCTION.
                </TextBold>
              </StyledParagraph>
              <StyledParagraph>
                Please make sure your teller is typing out both the account
                numbers and the address to ensure B-Stock receives payment.
              </StyledParagraph>
              <StyledParagraph>
                Please include the flat rate shipping cost displayed on the
                auction page and your Bid Won notification, in your total
                payment. B-Stock collects payment on behalf of Costco Wholesale,
                and we will notify Costco Wholesale once your order has been
                paid for.
              </StyledParagraph>
            </>
          ),
        },
        {
          question:
            'What forms of payment do you accept? Do you accept credit cards?',
          answer: (
            <>
              ACH and Wire Payments are the only forms of payment accepted. This
              is the standard practice in the sale of liquidation lots. We do
              not accept credit cards, personal checks or Western Union for
              payments.
            </>
          ),
        },
        {
          question: 'Can I Pay for Orders Using Account Credits?',
          answer: (
            <>
              Using our B-Stock Balance feature, you can pay for orders with
              account credit that you may have accrued. For more information
              please visit{' '}
              <StyledLink href="/b-stock-balance/">B-Stock Balance</StyledLink>.
            </>
          ),
        },
        {
          question: 'Can I combine multiple payments together?',
          answer: (
            <>
              You may always combine multiple payments into one wire for
              auctions won within 2 days of each other, if you are complying
              with the payment policy. Please be sure to put all order numbers
              in the memo section of the wire.
            </>
          ),
        },
        {
          question: 'What do I do after I have paid?',
          answer: (
            <>
              <StyledParagraph>
                After your payment has been confirmed, your order will be sent
                to the Carrier handling your shipment.
              </StyledParagraph>
              <StyledParagraph>
                The Carrier will contact you to schedule a delivery after they
                pick up your shipment from the Seller&apos;s facility.
              </StyledParagraph>
              <StyledParagraph>
                You will receive an &quot;Order Shipped&quot; notification after
                the shipment has left the Seller&apos;s facility and the Carrier
                will contact you to schedule delivery, usually within 2 days of
                the notification.
              </StyledParagraph>
              <StyledParagraph>
                You may also track the status of your shipment on your Orders
                page. View the details of your order to see additional shipment
                tracking information.
              </StyledParagraph>
            </>
          ),
        },
        {
          question: 'How does shipping work?',
          answer: (
            <>
              <StyledParagraph>
                B-Stock offers 3 different types of shipping:
              </StyledParagraph>
              <List>
                <ListItem>
                  <TextBold>Binding Shipping</TextBold>
                </ListItem>
                <ListItem>
                  <TextBold>Buyer Arranges Shipping</TextBold>
                </ListItem>
                <ListItem>
                  <TextBold>Fixed Price Shipping</TextBold>
                  <SubList>
                    <ListItem>
                      &quot;Free Shipping&quot; is a type of fixed price
                      shipping
                    </ListItem>
                  </SubList>
                </ListItem>
              </List>
              <StyledParagraph>
                The auction shipping type appears on the Auction Details page
                and in the Bid Confirmation dialog box.
              </StyledParagraph>
              <StyledParagraph>
                Shipping types may vary from listing to listing, even within the
                same marketplace.
              </StyledParagraph>
              <StyledParagraph>
                <TextBold>Binding Shipping</TextBold>
              </StyledParagraph>
              <StyledParagraph>
                Binding shipping means you, the Buyer, must pay the total order
                amount which is your winning bid plus the cost of shipping and
                tax, if applicable. Once paid, the Seller and Carrier partner
                arrange shipment of your Order.
              </StyledParagraph>
              <StyledParagraph>
                After your Order is &quot;Paid&quot;, your shipment is booked
                with the Carrier who will contact you to schedule a delivery.
              </StyledParagraph>
              <StyledParagraph>
                Buyers should schedule delivery within 3 days of a
                Carrier&apos;s suggested delivery date. Carriers may be able to
                accommodate later delivery times subject to additional holding
                fees.
              </StyledParagraph>
              <StyledParagraph>
                <TextBold>Buyer Arranges Shipping</TextBold>
              </StyledParagraph>
              <StyledParagraph>
                Buyers are responsible for arranging shipment with a Carrier of
                their choice and must work with the Seller to schedule pickup
                and delivery of their shipment.
              </StyledParagraph>
              <StyledParagraph>
                If additional information about scheduling a pickup from the
                Seller is available, it will be shown on the Auction Details
                page or in email notifications.
              </StyledParagraph>
              <StyledParagraph>
                <TextBold>Fixed Price</TextBold>
              </StyledParagraph>
              <StyledParagraph>
                Buyers are required to pay the fixed price for shipment shown
                during bidding and the Seller will arrange pickup of your Order
                with their Carrier.
              </StyledParagraph>
              <StyledParagraph>
                After your Order is &quot;Paid&quot;, your shipment is booked
                with the Carrier who will contact you to schedule a delivery.
              </StyledParagraph>
              <StyledParagraph>
                At this time, Costco offers a Truckload Discount Savings
                program. For information about program eligibility, see our FAQ
                below.
              </StyledParagraph>
            </>
          ),
        },
        {
          question: 'How does consolidated shipping work?',
          answer: (
            <>
              <StyledParagraph>
                To allow you an opportunity to save on shipping when you win
                multiple lots, we are offering a Consolidated Shipping Program.
              </StyledParagraph>
              <StyledParagraph>
                <TextBold>
                  Your Costco orders will automatically qualify for a single
                  truckload delivery if:
                </TextBold>
              </StyledParagraph>
              <List>
                <ListItem>18 pallet count threshold is met</ListItem>
                <ListItem>Shipping from the same facility</ListItem>
                <ListItem>Purchased within 48 hours</ListItem>
                <ListItem>Processed as paid the same day</ListItem>
              </List>
              <StyledParagraph>
                <TextBold>Example:</TextBold> 18 pallets across orders paid at
                the same time x $10/pallet = $180 B-Stock Credit. Credits will
                be processed 7 days after delivery.
              </StyledParagraph>
              <StyledParagraph>
                <em>Terms &amp; Conditions:</em>
              </StyledParagraph>
              <StyledParagraph>
                <em>
                  Costco consolidation eligibility requires a minimum combined
                  total of 18 pallets across orders paid at the same time.
                  Orders with a combined pallet count below 18 will not qualify
                  for a consolidation credit.
                </em>
              </StyledParagraph>
              <StyledParagraph>
                <em>
                  The credit amount is $10 per pallet (based on pallet count,
                  not space). For example, if two orders are consolidated with a
                  total of 20 pallets, the total credit will be $200, split
                  evenly across both orders ($100 per order).
                </em>
              </StyledParagraph>
            </>
          ),
        },
        {
          question:
            'Can I pick up the merchandise myself? Can I just pay when I pick it up?',
          answer: (
            <>
              <StyledLink href="https://bstock.com/buy/seller/costco">
                Costco Liquidations
              </StyledLink>{' '}
              will arrange shipping of your merchandise. Buyers can&apos;t
              arrange their own pick ups.
            </>
          ),
        },
        {
          question: 'Do I pay you for shipping?',
          answer: (
            <>
              You are responsible for payment of shipping cost for your order.
              Please include the shipping cost in your payment based on the flat
              rate shipping cost displayed on the auction page and sent to you
              in your Bid Won notification.
            </>
          ),
        },
        {
          question: 'What if there is a problem with my order?',
          answer: (
            <>
              All merchandise on{' '}
              <StyledLink href="https://bstock.com/buy/seller/costco">
                Costco Liquidations
              </StyledLink>{' '}
              is sold &quot;as is&quot;, and all sales are final. Keep this in
              mind when bidding on auctions, as merchandise cannot be returned.
            </>
          ),
        },
      ],
    },
    {
      title: 'Other Questions & Troubleshooting',
      questions: [
        {
          question:
            'Am I buying this merchandise directly from Costco Wholesale?',
          answer: (
            <>
              Yes! All merchandise on{' '}
              <StyledLink href="https://bstock.com/buy/seller/costco">
                Costco Liquidations
              </StyledLink>{' '}
              is coming directly from Costco Wholesale. All orders are shipped
              from their location(s).
            </>
          ),
        },
        {
          question: 'Where is the auction merchandise located?',
          answer: (
            <>
              The auctions on{' '}
              <StyledLink href="https://bstock.com/buy/seller/costco">
                Costco Liquidations
              </StyledLink>{' '}
              are primarily located in Monroe Township, NJ. Each auction title
              and description provides the physical location of the merchandise.
            </>
          ),
        },
        {
          question: 'How often are there new auctions?',
          answer: (
            <>
              New auctions can be posted at any time during normal business
              hours. Buyers that are subscribed to the{' '}
              <StyledLink href="https://bstock.com/buy/seller/costco">
                Costco Liquidations
              </StyledLink>{' '}
              newsletter will receive an email when auctions are opening and
              closing each day.
            </>
          ),
        },
        {
          question:
            'How can I update my personal information / change my password?',
          answer: (
            <>
              As an approved buyer, if you are logged into{' '}
              <StyledLink href={myAccountSettingsBase(myAccountHpNav)}>
                your account
              </StyledLink>{' '}
              select &quot;personal settings&quot; . You can update all contact
              information or change the password on your account. You can also
              click &quot;edit&quot; on the login page if you cannot remember
              your password.
            </>
          ),
        },
        {
          question: 'How do I report a technical problem?',
          answer: (
            <>
              <StyledLink href="/contact-us/">Send us an email</StyledLink> to
              report your issue. Please be as specific as possible and be sure
              to include any information that would help us determine the cause
              of your problem, including: which web browser you are using
              (Internet Explorer, Chrome, etc.), the web page you are on, steps
              to replicate the issue you are having, and other details and
              screenshots for our review.
            </>
          ),
        },
        {
          question:
            'I am approved and having trouble logging in. What can I do?',
          answer: (
            <>
              <StyledParagraph>
                Sometimes your browser can cause login problems. Please try the
                following fixes:
              </StyledParagraph>
              <OrderedList>
                <ListItem>
                  <StyledLink href="http://www.wikihow.com/Clear-Your-Browser%27s-Cache">
                    Clear your browser&apos;s cache
                  </StyledLink>
                  , then close it and reopen it and try logging in again.
                </ListItem>
                <ListItem>
                  Try logging in with a different browser. We recommend using
                  the latest version of{' '}
                  <StyledLink href="https://www.firefox.com/en-US/">
                    Firefox
                  </StyledLink>
                  ,{' '}
                  <StyledLink href="https://www.google.com/intl/en/chrome/browser/">
                    Chrome
                  </StyledLink>{' '}
                  or{' '}
                  <StyledLink href="http://support.apple.com/downloads/#safari">
                    Safari
                  </StyledLink>
                  .
                </ListItem>
              </OrderedList>
            </>
          ),
        },
        {
          question:
            'When I try to log in, I see the message "Invalid login or password." Why?',
          answer: (
            <>
              If you receive this notification, then you either have not yet
              applied on{' '}
              <StyledLink href="https://bstock.com/buy/seller/costco">
                Costco Liquidations
              </StyledLink>
              , or you are entering an incorrect password. You can reset your
              password by clicking the &quot;I forgot&quot; link below your
              password entry and entering your email address. If you have not
              applied, then you can click the{' '}
              <StyledLink
                href="/acct/signup?siteAbb=cst"
                onClick={() => trackSignUpStart('costco_faq')}
              >
                Create An Account
              </StyledLink>{' '}
              button to begin your application.
            </>
          ),
        },
        {
          question:
            'When I try to log in, I see the message "Your application is still pending." What do I do?',
          answer:
            "If you receive this notification, then we have either not yet reviewed your application or you have not submitted us the proper reseller's certificate. If it has been more than 1 business day since you applied, then we are still waiting on the proper documentation from you. You can send us your reseller's certificate or fax it to (650) 241-3328.",
        },
      ],
    },
  ] satisfies FaqSection[]

type FaqDetailsProps = {
  sections: FaqSection[]
}

const FaqDetails = ({ sections }: FaqDetailsProps) => {
  return (
    <Container>
      {sections.map(({ title, questions }) => (
        <Section key={title}>
          <SubHeader>{title}</SubHeader>
          {questions.map(({ question, answer }) => (
            <StyledAccordionWrapper key={`${title}::${question}`}>
              <Accordion
                label={
                  <QuestionLabel>
                    <StyledIcon icon={faQuestionCircle} />
                    <span>{question}</span>
                  </QuestionLabel>
                }
              >
                <AnswerContainer>{answer}</AnswerContainer>
              </Accordion>
            </StyledAccordionWrapper>
          ))}
        </Section>
      ))}
    </Container>
  )
}

export default FaqDetails
